"use client";

import { cn } from "@/lib/tailwind";
import { getDirFromLocale } from "@/utils/common";
import { EmblaCarouselType } from "embla-carousel";
import Autoplay, { AutoplayOptionsType } from "embla-carousel-autoplay";
import useEmblaCarousel from "embla-carousel-react";
import { useParams } from "next/navigation";
import { ReactNode, useCallback, useEffect, useState, useRef } from "react";

type EmblaOptions = Parameters<typeof useEmblaCarousel>[0];

export type Slide = {
  slideClasses?: string;
  content: ReactNode;
  /** Stable key for React reconciliation (e.g. product/variant id). Falls back to slide index. */
  slideKey?: string;
};

export type Control = {
  distance?: `${number}px`;
  verticalDist?: `${number}%`;
  left?: {
    content?: ReactNode;
    top?: `${number}%`;
  };
  right?: {
    content?: ReactNode;
    top?: `${number}%`;
  };
};

interface IProps {
  /** Represents the space between each carousel item */
  spacing?: number;

  /** The carousel items to display inside the carousel */
  slides: Slide[];

  /** Carousel options representing the options in Embla carousel */
  carouselOptions?: EmblaOptions;

  /** Carousel buttons to control the behaviour of the carousel */
  controls?: Control;

  /** auto play controls */
  autoPlay?: AutoplayOptionsType;

  /** show pagination controls */
  paginationControls?: {
    classname?: string;
    showPaginationOnAfterIndex?: boolean;

  };

  parentClasses?: string;
  slideParentClasses?: string;

  /** To get current image index. */
  onScrollSnap?: (indeX: number) => void;

  shouldScrollToStart?: boolean;

  onSildesInView?: (slidesInView: number[]) => void;

  /** Added for MEPS-1016: When only one slide is present, assume it is in view and call the in-view callback */
  assumeSingleSlideInView?: boolean;

  /** When true, do not register Embla Autoplay (manual arrows / swipe only). */
  disableAutoplay?: boolean;

  /**
   * When set (>= 0), scrolls the viewport to this slide index when it changes
   * (e.g. user selects a gift that is off-screen).
   */
  scrollToSlideIndex?: number;

  /**
   * When true, Embla stays active even if overflow is not detected yet (e.g. narrow summary column).
   * Does not affect arrow visibility — controls still require scrollable overflow.
   */
  forceActive?: boolean;
}

export const Carousel = ({
  slides,
  carouselOptions = { align: "start" },
  controls,
  spacing,
  autoPlay = { delay: 5000 },
  paginationControls,
  parentClasses,
  slideParentClasses,
  onScrollSnap,
  shouldScrollToStart,
  onSildesInView,
  assumeSingleSlideInView = false,
  disableAutoplay = false,
  scrollToSlideIndex,
  forceActive = false,
}: IProps) => {
  // states
  const [slidesInView, setSlidesInView] = useState<number[]>([]);
  const [shouldShowControls, setShouldShowControls] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  // hooks
  const params = useParams();
  const dir = getDirFromLocale(params.locale as string);

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  // hooks
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      ...carouselOptions,
      direction: dir,
      align: dir === "ltr" ? "start" : "end",
      active: forceActive ? true : shouldShowControls,
    },
    disableAutoplay ? [] : [Autoplay({ ...autoPlay })],
  );

  // methods
  const scrollPrev = useCallback(() => {
    if (emblaApi?.canScrollPrev()) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi?.canScrollNext()) emblaApi.scrollNext();
  }, [emblaApi]);

  const onDotButtonClick = useCallback(
    (index: number) => {
      if (!emblaApi) return;
      emblaApi.scrollTo(index);
    },
    [emblaApi],
  );

  const onInit = useCallback((emblaApi: EmblaCarouselType) => {
    setScrollSnaps(emblaApi.scrollSnapList());
  }, []);

  const updateScrollState = useCallback((api: EmblaCarouselType) => {
    const prev = api.canScrollPrev();
    const next = api.canScrollNext();
    setCanScrollPrev(prev);
    setCanScrollNext(next);
    setShouldShowControls(prev || next);
  }, []);

  const onSelect = useCallback(
    (emblaApi: EmblaCarouselType) => {
      const index = emblaApi.selectedScrollSnap();
      setSelectedIndex(index);
      onScrollSnap?.(index);
      updateScrollState(emblaApi);
    },
    [onScrollSnap, updateScrollState],
  );

  const updateSlidesInView = useCallback((emblaApi: EmblaCarouselType) => {
    setSlidesInView((slidesInView) => {
      if (slidesInView.length === emblaApi.slideNodes().length) {
        emblaApi.off("slidesInView", updateSlidesInView);
      }
      const inView = emblaApi.slidesInView().filter((index) => !slidesInView.includes(index));
      return slidesInView.concat(inView);
    });
  }, []);

  function getRightMargin(idx: number) {
    if (dir === "ltr" && carouselOptions.loop && slides.length - 1 === idx) {
      return spacing ?? 0;
    }
    return undefined;
  }

  function getLeftMargin(idx: number) {
    if (dir === "rtl" && carouselOptions.loop && slides.length - 1 === idx) {
      return spacing ?? 0;
    }
    return undefined;
  }

  // effects

  useEffect(() => {
    const checkOverflow = () => {
      const container = containerRef.current;
      const track = trackRef.current;
      if (!container || !track) return;

      setShouldShowControls(track.scrollWidth > container.clientWidth + 1);
    };

    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(checkOverflow, 100);
    };

    let resizeTimer: NodeJS.Timeout;
    checkOverflow();

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(resizeTimer);
    };
  }, [slides.length]);

  useEffect(() => {
    if (!emblaApi) return;

    updateScrollState(emblaApi);
    emblaApi.on("reInit", updateScrollState).on("resize", updateScrollState);

    return () => {
      emblaApi.off("reInit", updateScrollState).off("resize", updateScrollState);
    };
  }, [emblaApi, slides.length, updateScrollState]);

  useEffect(() => {
    if (!emblaApi) return;
    onInit(emblaApi);
    onSelect(emblaApi);
    emblaApi.on("reInit", onInit).on("reInit", onSelect).on("select", onSelect);
  }, [emblaApi, onInit, onSelect]);

  useEffect(() => {
    if (!emblaApi || !shouldScrollToStart) return;
    emblaApi.scrollTo(0);
  }, [shouldScrollToStart, emblaApi]);

  useEffect(() => {
    if (!emblaApi || typeof scrollToSlideIndex !== "number" || scrollToSlideIndex < 0) return;
    const max = Math.max(0, slides.length - 1);
    const index = Math.min(scrollToSlideIndex, max);
    if (emblaApi.selectedScrollSnap() === index) return;
    const id = requestAnimationFrame(() => {
      emblaApi.scrollTo(index);
    });
    return () => cancelAnimationFrame(id);
  }, [emblaApi, scrollToSlideIndex, slides.length]);

  useEffect(() => {
    if (!emblaApi) return;

    //Added for MEPS-1016: When only one slide is present, call the onSildesInView callback
    if (assumeSingleSlideInView && slides.length === 1) {
      setSlidesInView([0]); // manually say: "slide 0 is in view"
      onSildesInView?.([0]); 
      return;
    }

    updateSlidesInView(emblaApi);
    emblaApi.on("slidesInView", updateSlidesInView);
    emblaApi.on("reInit", updateSlidesInView);
  }, [emblaApi, updateSlidesInView]);

  useEffect(() => {
    onSildesInView?.(slidesInView);
  }, [slidesInView.length]);

  const showEdgeControls = slides.length > 1 && Boolean(controls?.left || controls?.right);

  const edgeControlWrapperClass = (enabled: boolean) =>
    cn(
      "absolute transition-all duration-200 [&_svg]:h-auto [&_svg]:w-auto",
      enabled
        ? "cursor-pointer opacity-100 [&_svg]:text-black"
        : "pointer-events-none cursor-default opacity-35 [&_svg]:text-neutral-400",
    );

  return (
    <div className={cn("relative", parentClasses)} ref={containerRef}>
      <div ref={emblaRef} className="h-full overflow-hidden">
        <div
          ref={trackRef}
          className={cn("flex h-full rtl:flex-row", slideParentClasses)}
          style={{
            gap: spacing ?? 0,
          }}
        >
          {slides.map((slide, idx) => (
            <div
              key={slide.slideKey ?? idx}
              style={{
                marginRight: getRightMargin(idx),
                marginLeft: getLeftMargin(idx),
              }}
              className={cn("min-w-0 flex-shrink-0 grow-0 basis-[80%]", slide.slideClasses)}
            >
              {slide.content}
            </div>
          ))}
        </div>
      </div>
      {showEdgeControls && controls?.left ? (
        <div
          className={edgeControlWrapperClass(canScrollPrev)}
          role="button"
          tabIndex={canScrollPrev ? 0 : -1}
          aria-disabled={!canScrollPrev}
          onClick={scrollPrev}
          style={{
            top: controls?.verticalDist ? controls.verticalDist : (controls?.left?.top ?? "50%"),
            left: controls?.distance ?? 0,
            transform: "translateY(-50%)",
          }}
        >
          {controls?.left?.content ?? "prev"}
        </div>
      ) : null}
      {showEdgeControls && controls?.right ? (
        <div
          className={edgeControlWrapperClass(canScrollNext)}
          role="button"
          tabIndex={canScrollNext ? 0 : -1}
          aria-disabled={!canScrollNext}
          onClick={scrollNext}
          style={{
            top: controls?.verticalDist ? controls.verticalDist : (controls?.right?.top ?? "50%"),
            right: controls?.distance ?? 0,
            transform: "translateY(-50%)",
          }}
        >
          {controls?.right?.content ?? "next"}
        </div>
      ) : null}

      {paginationControls && showEdgeControls && (
        <div
          className={cn(
            "mt-6 md:mt-8 flex transform items-center duration-300 ease-in-out md:hidden ",
            slides?.length > 1 ? "opacity-100" : "opacity-0",
            paginationControls?.classname,
            paginationControls?.showPaginationOnAfterIndex && selectedIndex === 0 ? "opacity-0" : "",
          )}
        >
          {scrollSnaps.map((snap, i) => (
            <div
              key={i}
              onClick={() => {
                onDotButtonClick(i);
              }}
              className={cn(
                "flex-1",
                selectedIndex === i ? "h-[3px] bg-black" : "h-[1px] bg-[rgba(212,212,212,1)]",
              )}
            ></div>
          ))}
        </div>
      )}
    </div>
  );
};
